/**
 * Apply a paragraph split or merge to the live v2 `XmlText` (LG-2859).
 *
 * Marker insert and capitalization share one transaction so a later undo step can treat them as one
 * edit. `applyXmlTextEdits` cannot insert a `Y.Map` or mix a delete with an insert.
 */

import * as Y from 'yjs'

import { MARKER_KINDS } from 'src/models/crdtV2'
import type { ParagraphStructurePlan, Qac } from 'src/models/crdtV2'

import { LOCAL_EDITOR_ORIGIN } from './xmlTextEdits'

export type ParagraphSplitMarker = {
    id: string
    speakerId: string
    qac?: Qac
}

const inLocalTransaction = (text: Y.XmlText, apply: () => void): void => {
    const doc = text.doc
    if (doc === null) {
        apply()

        return
    }

    doc.transact(apply, LOCAL_EDITOR_ORIGIN)
}

/** Insert one `paragraph_change` and, when the plan asks, replace its first code point. */
export const applyParagraphSplit = (text: Y.XmlText, plan: ParagraphStructurePlan, marker: ParagraphSplitMarker): void => {
    inLocalTransaction(text, () => {
        const map = new Y.Map<unknown>()
        map.set('kind', MARKER_KINDS.paragraphChange)
        map.set('id', marker.id)
        map.set('speakerId', marker.speakerId)
        if (marker.qac !== undefined) {
            map.set('qac', marker.qac)
        }
        text.insertEmbed(plan.markerIndex, map)
        if (plan.capitalize !== undefined) {
            text.delete(plan.capitalize.index, plan.capitalize.from.length)
            text.insert(plan.capitalize.index, plan.capitalize.to)
        }
    })
}

/** Delete the one boundary embed at `plan.markerIndex`. */
export const applyParagraphMerge = (text: Y.XmlText, plan: ParagraphStructurePlan): void => {
    inLocalTransaction(text, () => {
        text.delete(plan.markerIndex, 1)
    })
}
